import React, { createContext, useCallback, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import { format } from 'date-fns';
import { enUS, pl } from 'date-fns/locale';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import {
  adsModelFamilies,
  advertiserFaviconUrl,
  formatDisplayUrl,
  safeExternalUrl,
} from '../helpers/AdsHelper';

/** Cell renderers shared by the Ads tables (breakdown tabs, By-prompt section, row expansions). */

export const PAGINATOR_TEMPLATE =
  'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown';

/** Favicons shown in an advertiser strip before collapsing into "+X". */
const MAX_ADVERTISERS_IN_STRIP = 5;

/**
 * Segmented view switch — the global `.switch-box` (Context analysis → Sentiment "over time / by
 * model"): text segments with an optional mask icon, the active one black. Reads as a control, unlike
 * text tabs or a plain FilterBox, which blend with the section headings.
 */
export const ViewSwitch = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; labelKey: string; icon?: string }[];
  value: T;
  onChange: (value: T) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="switch-box" role="group">
      {options.map((option) => (
        <span
          key={option.value}
          role="button"
          aria-pressed={value === option.value}
          className={`switch-box__item ${value === option.value ? 'switch-box__item--active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.icon && <span className={`${option.icon} ico--xs`} aria-hidden="true" />}
          {t(option.labelKey)}
        </span>
      ))}
    </div>
  );
};

/** Self-framed table placeholder (it IS the frame — never render it inside another card). */
export const TableSkeleton: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <Skeleton className={`skeleton--table ${compact ? 'skeleton--table-2' : ''}`}>
    <span />
    <span />
    {!compact && (
      <>
        <span />
        <span />
        <span />
      </>
    )}
  </Skeleton>
);

export const AdsEmpty: React.FC = () => (
  <EmptyPanel textKey="ads.emptyTitle" descriptionKey="ads.emptyDesc" />
);

/**
 * Request-failure state with a retry — without it a failed fetch ends as `data === undefined`,
 * which the tables would render as the "No ads detected" empty state (a lie).
 * `framed` in table sections, where the state stands in a framed table's place; the chart card
 * frames its own content.
 */
export const AdsErrorPanel: React.FC<{ onRetry: () => void; framed?: boolean }> = ({
  onRetry,
  framed = false,
}) => {
  const { t } = useTranslation();
  return (
    <div className={`ads-page__error ${framed ? 'ads-page__error--framed' : ''}`}>
      <EmptyPanel textKey="ads.errorTitle" descriptionKey="ads.errorDesc" />
      <button type="button" className="btn btn--sm btn--primary" onClick={() => onRetry()}>
        {t('retry')}
      </button>
    </div>
  );
};

/** What the page tooltip bubble shows + where its anchor sits. */
interface AdsTipPayload {
  text: string;
  anchor: DOMRect;
}

const AdsTipContext = createContext<(tip: AdsTipPayload | null) => void>(() => undefined);

/**
 * Page-level text tooltip: one fixed bubble portaled to <body>, published by `AdsTip` wrappers on
 * hover (the AdPreviewProvider mechanism). A PrimeReact `<Tooltip target=".class">` binds its
 * targets only when the component mounting it re-renders — rows added by the tables' internal
 * pagination/sort would never get one (same reason the ad preview card is a portal).
 */
export const AdsTooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tip, setTip] = useState<AdsTipPayload | null>(null);
  const publish = useCallback((next: AdsTipPayload | null) => setTip(next), []);

  let bubble: React.ReactNode = null;
  if (tip) {
    const { anchor } = tip;
    // Centered above the anchor (below when there is no room), center clamped to the viewport.
    const centerX = Math.max(170, Math.min(anchor.left + anchor.width / 2, window.innerWidth - 170));
    const showBelow = anchor.top < 48;
    const style: React.CSSProperties = showBelow
      ? { left: centerX, top: anchor.bottom + 6 }
      : { left: centerX, bottom: window.innerHeight - anchor.top + 6 };
    bubble = createPortal(
      <div className="ads-page__tip" style={style} role="tooltip">
        {tip.text}
      </div>,
      document.body
    );
  }

  return (
    <AdsTipContext.Provider value={publish}>
      {children}
      {bubble}
    </AdsTipContext.Provider>
  );
};

/** Inline wrapper that shows `text` in the page tooltip bubble while hovered. */
export const AdsTip: React.FC<{ text: string; className?: string; children: React.ReactNode }> = ({
  text,
  className = '',
  children,
}) => {
  const publish = useContext(AdsTipContext);
  return (
    <span
      className={`ads-page__tip-anchor ${className}`}
      onMouseEnter={(event) =>
        publish({ text, anchor: event.currentTarget.getBoundingClientRect() })
      }
      onMouseLeave={() => publish(null)}
    >
      {children}
    </span>
  );
};

/** `d MMM` in the UI language — e.g. "6 lip" / "6 Jul". */
export const useDayFormatter = (): ((iso: string) => string) => {
  const { i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('pl') ? pl : enUS;
  return (iso: string) => format(new Date(iso), 'd MMM', { locale: dateLocale });
};

/**
 * Advertiser favicon with the domain's initial underneath: favicon.is can take seconds (or fail), and
 * an empty frame reads as "nothing here" — the letter shows until the image paints and stays when it
 * never does. `size` picks the frame variant (`--lg` thumbnail fallback, `--framed` list rows).
 */
export const AdvertiserFavicon: React.FC<{
  faviconUrl: string | null;
  domain: string;
  size?: 'default' | 'lg' | 'framed';
  className?: string;
}> = ({ faviconUrl, domain, size = 'default', className = '' }) => {
  const [failed, setFailed] = useState(false);
  const sizeClass = size === 'default' ? '' : ` ads-page__favicon--${size}`;
  return (
    <span className={`ads-page__favicon${sizeClass} ${className}`}>
      <span className="ads-page__favicon-letter" aria-hidden="true">
        {domain.charAt(0).toUpperCase()}
      </span>
      {!failed && (
        <img
          className="ads-page__favicon-img"
          src={advertiserFaviconUrl(faviconUrl, domain)}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      )}
    </span>
  );
};

/**
 * Square creative thumbnail: the image captured with the ad when the channel exposed one, else the
 * advertiser favicon centered in the same frame. A broken image falls back to the favicon too.
 */
export const AdThumb: React.FC<{ imageUrl: string | null; faviconUrl: string | null; domain: string }> = ({
  imageUrl,
  faviconUrl,
  domain,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const image = imageFailed ? null : safeExternalUrl(imageUrl);

  return (
    <span className="ads-page__ad-thumb">
      {image ? (
        <img
          className="ads-page__ad-thumb-img"
          src={image}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <AdvertiserFavicon faviconUrl={faviconUrl} domain={domain} size="lg" />
      )}
    </span>
  );
};

/** What the preview card shows for the hovered creative + where the hovered cell sits. */
interface AdPreview {
  title: string;
  body: string;
  imageUrl: string | null;
  faviconUrl: string | null;
  domain: string;
  advertiser?: string;
  targetUrl?: string | null;
  anchor: DOMRect;
}

const AdPreviewContext = createContext<(preview: AdPreview | null) => void>(() => undefined);

const PREVIEW_WIDTH = 360;

/**
 * One fixed-position preview card for the whole page: the hovered
 * `AdCopyCell` publishes its creative + rect, the card renders above it (below when there is no room)
 * through a portal to <body>, so table overflow/stacking contexts cannot clip it. One element instead
 * of one PrimeReact Tooltip per row (the creatives table can hold up to 1000 rows).
 */
export const AdPreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<AdPreview | null>(null);
  const publish = useCallback((next: AdPreview | null) => setPreview(next), []);

  let card: React.ReactNode = null;
  if (preview) {
    const { anchor } = preview;
    const left = Math.max(8, Math.min(anchor.left, window.innerWidth - PREVIEW_WIDTH - 8));
    const showBelow = anchor.top < 240;
    const style: React.CSSProperties = showBelow
      ? { left, top: anchor.bottom + 8 }
      : { left, bottom: window.innerHeight - anchor.top + 8 };
    const image = safeExternalUrl(preview.imageUrl);
    const destination = preview.targetUrl ? formatDisplayUrl(preview.targetUrl) : null;
    card = createPortal(
      <div className="ads-page__ad-preview" style={style} role="tooltip">
        <div className="ads-page__ad-preview-head">
          <AdvertiserFavicon faviconUrl={preview.faviconUrl} domain={preview.domain} />
          <span className="ads-page__ad-preview-advertiser">{preview.advertiser || preview.domain}</span>
          <span className="ads-page__ad-preview-sponsored">· {t('ads.sponsoredLabel')}</span>
        </div>
        <div className="ads-page__ad-preview-body">
          <div className="ads-page__ad-preview-text">
            <div className="ads-page__ad-preview-title">{preview.title}</div>
            {preview.body && <div className="ads-page__ad-preview-desc">{preview.body}</div>}
          </div>
          {image && <img className="ads-page__ad-preview-img" src={image} alt="" referrerPolicy="no-referrer" />}
        </div>
        {destination && <div className="ads-page__ad-preview-url">{destination}</div>}
      </div>,
      document.body
    );
  }

  return (
    <AdPreviewContext.Provider value={publish}>
      {children}
      {card}
    </AdPreviewContext.Provider>
  );
};

/** One-line ad copy in the table; hovering it shows the full creative in the page's preview card. */
export const AdCopyCell: React.FC<{
  title: string;
  body: string;
  imageUrl: string | null;
  faviconUrl: string | null;
  domain: string;
  advertiser?: string;
  targetUrl?: string | null;
}> = ({ title, body, imageUrl, faviconUrl, domain, advertiser, targetUrl }) => {
  const publishPreview = useContext(AdPreviewContext);

  return (
    <div
      className="ads-page__ad-copy"
      onMouseEnter={(event) =>
        publishPreview({
          title,
          body,
          imageUrl,
          faviconUrl,
          domain,
          advertiser,
          targetUrl,
          anchor: event.currentTarget.getBoundingClientRect(),
        })
      }
      onMouseLeave={() => publishPreview(null)}
    >
      <AdThumb imageUrl={imageUrl} faviconUrl={faviconUrl} domain={domain} />
      <div className="ads-page__ad-copy-text">
        <div className="ads-page__ad-copy-title">{title}</div>
        {body && <div className="ads-page__ad-copy-body">{body}</div>}
      </div>
    </div>
  );
};

/** Icon-only model badges — the name shows in the tooltip (the app-wide pattern). */
export const ModelBadges: React.FC<{ types: string[] }> = ({ types }) => (
  <div className="ads-page__model-badges">
    {adsModelFamilies(types).map((family) => (
      <AdsTip key={family.key} text={family.label} className="ads-page__model-badge">
        <span className={`${family.icon} ico--sm`} />
      </AdsTip>
    ))}
  </div>
);

/** Framed favicon + advertiser name (+ the "You" badge for the project's own domain). */
export const AdvertiserCell: React.FC<{
  advertiser: string;
  domain: string;
  faviconUrl: string | null;
  isYou?: boolean;
}> = ({ advertiser, domain, faviconUrl, isYou }) => {
  const { t } = useTranslation();
  return (
    <div className="ads-page__advertiser-cell">
      <AdvertiserFavicon faviconUrl={faviconUrl} domain={domain} size="framed" />
      <span className="ads-page__advertiser-name" title={domain}>
        {advertiser}
      </span>
      {isYou && <span className="ads-page__you-badge">{t('ads.youBadge')}</span>}
    </div>
  );
};

/**
 * Text link with the external-link glyph (the Sources domain/source pattern). Without an http(s)
 * href — scraped targetUrls may carry any scheme — it renders as plain text, never as a link.
 */
export const ExternalLink: React.FC<{ href: string | null; label: string; title: string }> = ({
  href,
  label,
  title,
}) =>
  href ? (
    <a
      className="ads-page__link"
      href={href}
      title={title}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="ads-page__link-text">{label}</span>
      <span className="ico--sm ico--external-link ads-page__link-ico" />
    </a>
  ) : (
    <span className="ads-page__link" title={title}>
      <span className="ads-page__link-text">{label}</span>
    </span>
  );

export const DomainLink: React.FC<{ domain: string }> = ({ domain }) => (
  <ExternalLink href={`https://${domain}`} label={domain} title={domain} />
);

/** Host + path as the label (tracking query stripped), full URL in href/title. */
export const TargetUrlLink: React.FC<{ targetUrl: string }> = ({ targetUrl }) => (
  <ExternalLink href={safeExternalUrl(targetUrl)} label={formatDisplayUrl(targetUrl)} title={targetUrl} />
);

/** Favicon strip of the advertisers seen at a prompt (most active first) + "+X" for the rest. */
export const AdvertiserStrip: React.FC<{
  domains: string[];
  faviconByDomain: Map<string, string | null>;
}> = ({ domains, faviconByDomain }) => {
  const visible = domains.slice(0, MAX_ADVERTISERS_IN_STRIP);
  const rest = domains.length - visible.length;
  return (
    <div className="ads-page__advertiser-strip">
      {visible.map((domain) => (
        <AdsTip key={domain} text={domain}>
          <AdvertiserFavicon
            faviconUrl={faviconByDomain.get(domain) ?? null}
            domain={domain}
            size="framed"
          />
        </AdsTip>
      ))}
      {rest > 0 && (
        <AdsTip text={domains.slice(MAX_ADVERTISERS_IN_STRIP).join(', ')}>
          <span className="ads-page__strip-more">+{rest}</span>
        </AdsTip>
      )}
    </div>
  );
};
